import { axiosInstance } from "../axios.config";

export type UserRole = 'buyer' | 'seller' | 'operator' | 'both';

/** PRD 1.1 / 2.3 / 4.8: member type, buyer type and consents */
export interface MemberFieldsPayload {
  affiliateType?: string;
  buyerType?: string;
  primaryIntent?: string;
  smsOptIn?: boolean;
  callConsent?: boolean;
}

export interface CreateUserProfilePayload extends MemberFieldsPayload {
  termsAccepted: boolean;
  /** PRD 2.6: answered "No, that's not me" to a name match */
  duplicateCertified?: boolean;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  role: UserRole;
  businessName?: string;
  bio?: string;
}

export interface UpdateUserProfilePayload extends MemberFieldsPayload {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  bio?: string;
  role?: UserRole;
}

export interface NotificationPreferencesResponse {
  buyBoxMatchesEmail: boolean;
  newBidsEmail: boolean;
  bidStatusChangesEmail: boolean;
  newMessagesEmail: boolean;
  listingBackOnMarketEmail: boolean;
  buyBoxMatchesDigest: boolean;
}

export interface UpdateNotificationPreferencesPayload {
  buyBoxMatchesEmail?: boolean;
  newBidsEmail?: boolean;
  bidStatusChangesEmail?: boolean;
  newMessagesEmail?: boolean;
  listingBackOnMarketEmail?: boolean;
  buyBoxMatchesDigest?: boolean;
}

export interface LicenseResponse {
  id: string;
  licenseNumber: string;
  licenseType: 'Agent' | 'Broker';
  state: string;
  createdAt: string;
  updatedAt: string;
}

export interface UserProfileResponse {
  id: string;
  /** Master §2 member type; null = not picked yet (asked on next login) */
  affiliateType?: string | null;
  buyerType?: string | null;
  primaryIntent?: string | null;
  smsOptIn?: boolean;
  callConsent?: boolean;
  /** "In Marketplace – brought by …" */
  broughtByName?: string | null;
  flipiqAuthUserId: string;
  activeOrganizationId: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  bio: string | null;
  photoUrl: string | null;
  role: UserRole;
  emailVerified: boolean;
  hasSeenVerificationPrompt: boolean;
  referredByUserId: string | null;
  tier: number;
  tierName: string;
  verifiedEntityCount: number;
  isLicensed: boolean;
  licenses: LicenseResponse[];
  dealsAsSeller: number;
  dealsAsBuyer: number;
  totalDeals: number;
  createdAt: string;
  updatedAt: string;
}

export interface PublicUserProfileResponse {
  id: string;
  firstName: string;
  lastName: string;
  photoUrl: string | null;
  bio: string | null;
  tier: number;
  tierName: string;
  isLicensed: boolean;
  dealsAsSeller: number;
  dealsAsBuyer: number;
  totalDeals: number;
  memberSince: string;
}

export const userService = {
  /**
   * Create user profile (first-time login)
   *
   * @param data - Profile data
   * @param invitationToken - Optional invitation token for referral tracking
   */
  createProfile: async (
    data: CreateUserProfilePayload,
    invitationToken?: string
  ): Promise<UserProfileResponse> => {
    // Build URL with optional invitation query parameter
    let url = "/users/profile";
    if (invitationToken) {
      url += `?invitation=${encodeURIComponent(invitationToken)}`;
    }

    const response = await axiosInstance.post<UserProfileResponse>(
      url,
      data
    );
    return response.data;
  },

  /**
   * Get current user profile
   */
  getProfile: async (): Promise<UserProfileResponse> => {
    const response =
      await axiosInstance.get<UserProfileResponse>("/users/profile");
    return response.data;
  },

  /**
   * Update user profile
   */
  updateProfile: async (
    data: UpdateUserProfilePayload
  ): Promise<UserProfileResponse> => {
    const response = await axiosInstance.put<UserProfileResponse>(
      "/users/profile",
      data
    );
    return response.data;
  },

  /**
   * Get presigned URL for uploading profile photo
   */
  getPhotoUploadUrl: async (): Promise<{ uploadUrl: string; photoKey: string }> => {
    const response = await axiosInstance.post<{ uploadUrl: string; photoKey: string }>(
      "/users/profile/photo/upload-url"
    );
    return response.data;
  },

  /**
   * Update profile photo after successful S3 upload
   */
  updateProfilePhoto: async (photoKey: string): Promise<UserProfileResponse> => {
    const response = await axiosInstance.put<UserProfileResponse>(
      "/users/profile/photo",
      { photoKey }
    );
    return response.data;
  },

  /**
   * Delete profile photo
   */
  deleteProfilePhoto: async (): Promise<void> => {
    await axiosInstance.delete("/users/profile/photo");
  },

  /**
   * Get notification preferences
   */
  getNotificationPreferences: async (): Promise<NotificationPreferencesResponse> => {
    const response = await axiosInstance.get<NotificationPreferencesResponse>(
      "/users/me/notification-preferences"
    );
    return response.data;
  },

  /**
   * Update notification preferences
   */
  updateNotificationPreferences: async (
    data: UpdateNotificationPreferencesPayload
  ): Promise<NotificationPreferencesResponse> => {
    const response = await axiosInstance.put<NotificationPreferencesResponse>(
      "/users/me/notification-preferences",
      data
    );
    return response.data;
  },

  /**
   * Mark verification prompt as seen
   */
  markVerificationPromptSeen: async (): Promise<{ success: boolean }> => {
    const response = await axiosInstance.put<{ success: boolean }>(
      "/users/verification-prompt-seen"
    );
    return response.data;
  },

  /**
   * Get public user profile (no auth required)
   * Used for viewing other users' reputation
   */
  getPublicProfile: async (userId: string): Promise<PublicUserProfileResponse> => {
    const response = await axiosInstance.get<PublicUserProfileResponse>(
      `/users/${userId}/public`
    );
    return response.data;
  },
};